import { useEffect, useRef } from 'react';
import { AppState, Platform } from 'react-native';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { supabase } from '../../shared/supabase/supabaseClient';
import { isRealtimeMastersEnabled } from '../infra/realtimeMastersConfig';
import { canRunOperationalBackgroundWork } from '../../shell/session/accountSetupService';
import { recordNetworkCall } from '../../shared/dev/networkCallAudit';
import { refreshRemoteDomains } from '../core/refreshRemoteDomains';

const REALTIME_DEBOUNCE_MS = isRealtimeMastersEnabled() ? 3000 : 10_000;

/**
 * Realtime en maestros → dispara refreshRemoteDomains (debounced) solo catálogo.
 * Deshabilitado por default (EXPO_PUBLIC_REALTIME_MASTERS=1 para opt-in).
 * Visible-only: no suscribe con app/tab en background.
 */
export function useSyncRealtimeRefresh(enabled: boolean) {
  const userId = useAuthStore((s) => s.session?.user?.id);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visibleRef = useRef(true);

  useEffect(() => {
    if (!enabled || !userId || !isRealtimeMastersEnabled()) return;

    const scheduleRefresh = () => {
      if (!visibleRef.current) return;
      if (!canRunOperationalBackgroundWork()) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void refreshRemoteDomains();
      }, REALTIME_DEBOUNCE_MS);
    };

    const channel = supabase
      .channel(`sync-masters-${userId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'org_category_settings' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'brands' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'suppliers' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'inventory', table: 'stocks' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'inventory', table: 'stock_transfers' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'inventory', table: 'stock_lots' }, scheduleRefresh)
      .subscribe((status) => {
        recordNetworkCall({
          class: 'realtime',
          category: 'realtime',
          name: `sync-masters-${status}`,
        });
      });

    const appSub = AppState.addEventListener('change', (state) => {
      visibleRef.current = state === 'active';
      if (state !== 'active' && timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    });

    let removeVisibility: (() => void) | undefined;
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const onVisibility = () => {
        visibleRef.current = document.visibilityState === 'visible';
        if (!visibleRef.current && timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
      document.addEventListener('visibilitychange', onVisibility);
      visibleRef.current = document.visibilityState === 'visible';
      removeVisibility = () => document.removeEventListener('visibilitychange', onVisibility);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      void supabase.removeChannel(channel);
      appSub.remove();
      removeVisibility?.();
      recordNetworkCall({
        class: 'realtime',
        category: 'realtime',
        name: 'sync-masters-unsubscribe',
      });
    };
  }, [enabled, userId]);
}
